import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * A cart button that displays the cart total and item count, and keeps them in
 * sync with the cart.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartTotal - The element holding the formatted total.
 * @property {HTMLElement} cartCount - The element holding the item count.
 * @property {HTMLElement} liveRegion - The live region announcing cart changes.
 *
 * @extends {Component<Refs>}
 */
class CartTotalButton extends Component {
  requiredRefs = ['cartTotal', 'cartCount', 'liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    event.promise
      ?.then(() => this.#refresh())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-total-button] Event promise rejected:', error);
      });
  };

  async #refresh() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) return;

    const cart = await response.json();
    const format = this.dataset.moneyFormat || '{{amount}}';

    this.refs.cartTotal.textContent = formatMoney(cart.total_price, format, cart.currency);
    this.refs.cartCount.textContent = `(${cart.item_count})`;
    this.classList.toggle('cart-total-button--has-cart', cart.item_count > 0);
    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cart.item_count}`;
  }
}

if (!customElements.get('cart-total-button')) {
  customElements.define('cart-total-button', CartTotalButton);
}
